import React, { FC, useState, useMemo, useCallback } from "react";
import BottomSheet from "shared/BottomSheet/BottomSheet";
import NcImage from "shared/NcImage/NcImage";
import { Product } from "data/data";
import { useCartDataProvider } from "context/CartProductContext";
import { validateModifiers, getModifierPrice } from "utils/modifierHelpers";
import ModifierSection from "./ModifierSection";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { ShoppingBagIcon } from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import { Transition } from "@headlessui/react";

export interface ProductCustomizationSheetProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  editMode?: {
    cartItemId: number;
    modifierChecked: Record<number, Record<number, boolean>>;
    quantity: number;
    note: string;
  };
}

const ProductCustomizationSheet: FC<ProductCustomizationSheetProps> = ({
  product,
  isOpen,
  onClose,
  editMode,
}) => {
  const { dispatch } = useCartDataProvider();

  // Initialize modifier state
  const [modifierState, setModifierState] = useState<
    Record<number, Record<number, boolean>>
  >(editMode?.modifierChecked || {});
  const [quantity, setQuantity] = useState(editMode?.quantity || 1);
  const [note, setNote] = useState(editMode?.note || "");
  const [showError, setShowError] = useState(false);

  // Reset state every time the sheet opens (even for the same product)
  const prevOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      // Sheet just opened -- reset all state
      setModifierState(editMode?.modifierChecked || {});
      setQuantity(editMode?.quantity || 1);
      setNote(editMode?.note || "");
      setShowError(false);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, product, editMode]);

  const handleModifierChange = useCallback(
    (modifierSetId: number, itemId: number, checked: boolean) => {
      setModifierState((prev) => {
        const updated = { ...prev };
        if (!updated[modifierSetId]) {
          updated[modifierSetId] = {};
        }
        if (checked) {
          updated[modifierSetId][itemId] = true;
        } else {
          delete updated[modifierSetId][itemId];
        }
        return updated;
      });
      setShowError(false);
    },
    []
  );

  // Calculate running total
  const runningTotal = useMemo(() => {
    if (!product) return 0;
    const basePrice = product.onlinePrice;
    const modPrice = getModifierPrice(product, modifierState);
    return (basePrice + modPrice) * quantity;
  }, [product, modifierState, quantity]);

  const handleAddToCart = () => {
    if (!product) return;

    const validation = validateModifiers(product, modifierState);
    if (!validation.valid) {
      setShowError(true);
      // Scroll to first error modifier section
      const firstErrorId = validation.errors[0]?.modifierSetId;
      if (firstErrorId) {
        const el = document.querySelector(`[data-modifier-set-id="${firstErrorId}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    const cartProduct = {
      ...product,
      quantity,
      modifierChecked: modifierState,
      note,
    };

    if (editMode) {
      dispatch({
        type: "EDIT_CART",
        payload: { ...cartProduct, cartItemId: editMode.cartItemId },
      });
    } else {
      dispatch({ type: "ADD_TO_CART", payload: cartProduct });
    }

    // Show success toast
    toast.custom(
      (t) => (
        <Transition
          appear
          show={t.visible}
          className="p-3 max-w-sm w-full bg-white dark:bg-slate-800 shadow-lg rounded-xl pointer-events-auto ring-1 ring-black/5 dark:ring-white/10 text-slate-900 dark:text-slate-100"
          enter="transition-all duration-200"
          enterFrom="opacity-0 translate-y-4"
          enterTo="opacity-100 translate-y-0"
          leave="transition-all duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-stone-100 dark:bg-slate-700">
              {product.imageUrl && (
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{product.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {editMode ? "Updated in cart" : "Added to cart"}
              </p>
            </div>
          </div>
        </Transition>
      ),
      { position: "bottom-center", id: `customize-${product.id}`, duration: 1500 }
    );

    onClose();
  };

  if (!product) return null;

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      {/* Product image */}
      <div className="relative w-full h-48 sm:h-56 bg-stone-100 dark:bg-slate-800">
        <NcImage
          containerClassName="w-full h-full"
          src={product.imageUrl || ""}
          className="object-cover w-full h-full"
        />
      </div>

      {/* Product info */}
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          {product.name}
        </h2>
        <p className="text-base font-bold text-primary-600 dark:text-primary-400 mt-1">
          ${product.onlinePrice.toFixed(2)}
          {product.unit?.name && (
            <span className="text-xs font-normal text-slate-400 ml-1">
              / {product.unit.name}
            </span>
          )}
        </p>
        {product.description && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            {product.description}
          </p>
        )}
      </div>

      {/* Modifier sections (all expanded, no accordions!) */}
      {product.modifierSets && product.modifierSets.length > 0 && (
        <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
          {product.modifierSets.map((modSet) => (
            <ModifierSection
              key={modSet.id}
              modifierSet={modSet}
              selectedState={modifierState[modSet.id] || {}}
              onChange={handleModifierChange}
              showError={showError}
            />
          ))}
        </div>
      )}

      {/* Note section */}
      <div className="px-4 py-3">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">
          Special Instructions
        </h4>
        <textarea
          placeholder="Any special requests?"
          className="w-full h-16 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent resize-none"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      {/* Sticky footer: quantity + add to cart */}
      <div className="sticky bottom-0 px-4 py-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] dark:shadow-[0_-4px_12px_rgba(0,0,0,0.3)]">
        <div className="flex items-center gap-3">
          {/* Quantity control */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-full px-1 py-1">
            <button
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors"
              disabled={quantity <= 1}
            >
              <MinusIcon className="w-5 h-5" />
            </button>
            <span className="w-8 text-center font-bold text-slate-900 dark:text-slate-100">
              {quantity}
            </span>
            <button
              onClick={() => setQuantity((q) => q + 1)}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Add to cart button with running total */}
          <button
            onClick={handleAddToCart}
            className="flex-1 h-12 flex items-center justify-center gap-2 bg-accent-500 dark:bg-accent-400 text-white dark:text-slate-900 rounded-xl font-bold text-base hover:bg-accent-600 dark:hover:bg-accent-500 transition-colors shadow-md active:scale-[0.98]"
          >
            <ShoppingBagIcon className="w-5 h-5" />
            <span>
              {editMode ? "Update" : "Add to Cart"} - $
              {runningTotal.toFixed(2)}
            </span>
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default ProductCustomizationSheet;
